import { useRef } from 'react';

import { sendFEEvent } from '../../../../../../../eventUtil';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { useAPI } from '../../../../../../../services/api.service';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import {
  ESdkEvents,
  EPaymentStates,
  OrderResponse,
  EventParams,
} from '../../../../../../../contracts';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';

const FETCH_INTERVAL_TIME = 2500;
const POLLING_DURATION_MS = 40000;

export function useAdyenOrderPolling() {
  const { API } = useAPI();
  const { startOrderPolling } = useOrderPolling();
  const interval = useRef<NodeJS.Timeout>();
  const timeout = useRef<NodeJS.Timeout>();
  const { getEventParams } = useCheckout();
  const setPublisherPurchaseId = usePaymentState((state) => state.setPublisherPurchaseId);

  const stopPolling = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = undefined;
    }
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  };

  const startAdyenOrderPolling = (
    orderId: string,
    handleErrorCallback?: () => void,
    FEeventParams: Partial<EventParams> = {},
  ) => {
    stopPolling();
    interval.current = setInterval(fetchOrderData, FETCH_INTERVAL_TIME, orderId, FEeventParams);
    timeout.current = setTimeout(handleTimeoutEnd, POLLING_DURATION_MS, handleErrorCallback);
  };

  const handleTimeoutEnd = (handleErrorCallback?: () => void) => {
    handleErrorCallback?.();
    stopPolling();
  };

  async function fetchOrderData(orderId: string, FEeventParams?: Partial<EventParams>) {
    try {
      const orderResponse = await API.getOrder(orderId);
      handleOrderResponse(orderResponse, orderId, FEeventParams);
    } catch (error: any) {
      console.error(error);
    }
  }

  function handleOrderResponse(
    order: OrderResponse,
    orderId: string,
    FEeventParams?: Partial<EventParams>,
  ) {
    if (order?.state !== EPaymentStates.PENDING) {
      setPublisherPurchaseId(order?.publisherPurchaseId);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(orderId);
      stopPolling();
    }
  }

  return { startAdyenOrderPolling };
}
